// 使用后端返回的权限字符串，不再依赖本地 ROLE_PERMISSIONS
import { resetRouter } from '@/router';
import { authService } from '@/services/authService';
import { defineStore } from 'pinia';

export interface UserInfo {
  id: number | null;
  roleId: number | null; // 单一主角色ID（如果后端返回）
  username: string | null;
  roleName: string | null; // 单一角色名称（后端返回的 role）
  // 统一存储后端返回的权限字符串（如 'EMPLOYEE_VIEW'）
  permissions: string[];
  token: string | null;
  employeeId: number | null; // 员工ID
}

export const useUserStore = defineStore('user', {
  state: (): UserInfo => ({
    id: null,
    roleId: null,
    username: null,
    roleName: null,
    permissions: [],
    token: null,
    employeeId: null,
  }),
  getters: {
    hasPermission:
      (state) =>
      (perm: string | string[]): boolean => {
        const list = Array.isArray(perm) ? perm : [perm];
        return list.every((p) => state.permissions.includes(p));
      },
  },
  actions: {
    initFromStorage() {
      const raw = localStorage.getItem('auth:user');
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw) as Partial<UserInfo>;
        this.id = parsed.id ?? null;
        this.roleId = parsed.roleId ?? null;
        this.username = parsed.username ?? null;
        this.roleName = parsed.roleName ?? null;
        this.employeeId = parsed.employeeId ?? null;
        // 从持久化中直接恢复权限字符串
        this.permissions = parsed.permissions ?? [];
        this.token = parsed.token ?? null;
      } catch {
        // ignore parse error
      }
    },
    setUser(payload: {
      id: number;
      roleId?: number | null;
      username: string;
      roleName: string; // 来自后端返回的 role 字段
      employeeId: number | null;
      permissions?: string[];
    }) {
      this.id = payload.id;
      this.roleId = payload.roleId ?? null;
      this.username = payload.username;
      this.roleName = payload.roleName;
      this.employeeId = payload.employeeId ?? null;
      // 使用后端返回的权限（如果提供）
      if (payload.permissions) this.permissions = payload.permissions;
      this.persist();
    },
    setToken(token: string) {
      this.token = token;
      this.persist();
    },
    persist() {
      const payload: Partial<UserInfo> = {
        id: this.id,
        roleId: this.roleId,
        username: this.username,
        roleName: this.roleName,
        permissions: this.permissions,
        token: this.token,
        employeeId: this.employeeId,
      };
      localStorage.setItem('auth:user', JSON.stringify(payload));
      if (this.token) localStorage.setItem('token', this.token);
      else localStorage.removeItem('token');
    },
    async logout() {
      // 尝试调用后端登出接口，不影响本地清理
      await authService.logout();
      this.$reset();
      resetRouter();
      localStorage.removeItem('auth:user');
      localStorage.removeItem('token');
    },
  },
});
